import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
//constants
import { pagesEnum, timing } from 'constants/ui.constants';
// @mui
import { Stack } from '@mui/system';
import { CircularProgress, Box } from '@mui/material';
// components
import { Page } from 'theme/elements/Page';
import { CenteredNarrowContent } from 'theme/elements/Content';
import CancelDialog from 'components/CancelDialog';
// sections
import Stepper from 'theme/elements/Stepper';
import CreateHeader from 'sections/pay/upsert/CreateHeader';
import Step1 from './steps/step1';
import Step2 from './steps/step2';
import Step3 from './steps/step3';
import Step4 from './steps/step4';
// reducers
import { useAppSelector, useAppDispatch } from 'hooks/redux.hooks';
import {
	toggleUpserting,
	getActiveStep,
	getSteps,
	upsertPayBeneficiary,
	setActiveStep,
	setPayementFees,
} from 'state/reducers/pay.reducers';
import { loadBeneficiaries } from 'services/beneficiary.services';
import { getBalancesByCompanyId } from 'services/balance.services';
import { Beneficiary } from 'constants/beneficiary.constants';
import { getBeneficiaries } from 'state/reducers/beneficiary.reducers';
import { PayEnum, PaymentStatus, paySteps } from 'constants/pay.constants';
import { format } from 'date-fns';
import { getFeesByCompanyId, getPaymentDates, getPaymentById as getPaymentByIdService } from 'services/pay.services';
import {
	cancelDialogProps,
	getHistory,
	getMobile,
	getUi,
	ifPageChange,
	setCheckBeforeNavigate,
	togglePage,
} from 'state/reducers/ui.reducers';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUrlBack } from 'theme/elements/Headers';
import { translatedCancelPopup } from 'utils';
import { getPaymentById } from 'state/reducers/requests.reducers';
import { getUserDetails, getUserPermission } from 'state/reducers/user.reducers';
import { User, UserPermission } from 'constants/user.constants';
import { getBalancesActivityByCompanyId } from 'services/activity.services';
import { Pay } from 'constants/pay.constants';

export default function PaySteps() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const user: User = useAppSelector(getUserDetails);
	const t = useAppSelector(selectTranslations);
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const [fullPaymentData, setFullPaymentData] = useState<(Record<string, any> & Pay) | undefined>();
	const [isLoadingPaymentData, setIsLoadingPaymentData] = useState(false);
	const activeStep = useAppSelector(getActiveStep);
	const steps = useAppSelector(getSteps);
	const isMobile = useAppSelector(getMobile);
	const beneficiaries: Beneficiary[] = useAppSelector(getBeneficiaries);
	const isLeavingPage = useAppSelector(ifPageChange);
	const ui = useAppSelector(getUi);
	const location = useLocation();
	const history = useAppSelector(getHistory);
	const urlback = getUrlBack(location.pathname, history[history.length - 1]);
	let { id, type } = useParams();
	const payment = useAppSelector(getPaymentById(id!));
	const userPermission = useAppSelector(getUserPermission);
	const stepProps = {
		handleCancel: () => setOpenCancelDialog(true),
	};
	const dialogProps = useAppSelector(cancelDialogProps);
	const dialogPropsTranslated = useMemo(() => translatedCancelPopup(dialogProps), [dialogProps]);

	useEffect(() => {
		let flag = activeStep > 0 && activeStep < 3 ? true : false;
		dispatch(setCheckBeforeNavigate(flag));
		window.scrollTo(0, 0);
	}, [activeStep]);

	useEffect(() => {
		if (isLeavingPage && ui.checkBeforeNavigate) {
			setOpenCancelDialog(true);
		}
	}, [isLeavingPage]);

	useEffect(() => {
		dispatch(loadBeneficiaries());
		dispatch(getBalancesByCompanyId());
		dispatch(getBalancesActivityByCompanyId(Number(user?.companyId)));
	}, []);

	useEffect(() => {
		if (type !== 'new' && id && userPermission === UserPermission.SIGNER) {
			// Set step immediately to avoid showing step 1
			dispatch(setActiveStep(paySteps.REVIEW));
			setIsLoadingPaymentData(true);

			dispatch(getPaymentByIdService(id)).then((res) => {
				if (res && res.status === PaymentStatus.AwaitingAuthorization) {
					setFullPaymentData({
						...res,
						paymentType: _.lowerCase(res.type),
						fee: res?.fee?.amount,
					});
					dispatch(
						upsertPayBeneficiary({
							beneficiary: res.beneficiary,
							[PayEnum.beneficiaryId]: res.beneficiary.id,
						})
					);
					dispatch(getPaymentDates(res.currencyId || '', format(new Date(), timing.DATE_FOR_SERVICE)));
				}
				setIsLoadingPaymentData(false);
			});
		}
	}, [userPermission, id, type]);
	useEffect(() => {
		if (id) {
			const obj = _.groupBy(beneficiaries, 'id');
			if (_.has(obj, id || '')) {
				let bn = _.get(obj, id || '')[0];
				dispatch(setPayementFees({}));
				dispatch(
					upsertPayBeneficiary({
						beneficiary: bn,
						[PayEnum.beneficiaryId]: +id,
					})
				);
				dispatch(getPaymentDates(bn.currencyId || '', format(new Date(), timing.DATE_FOR_SERVICE)));
				dispatch(getFeesByCompanyId(user.companyId, bn.currencyId));
			}
		}
	}, [beneficiaries, id]);

	return (
		<Fragment>
			<MobileNavbar
				handleBack={() => {
					if (activeStep > 0 && activeStep < 3) {
						dispatch(setActiveStep(_.values(paySteps)[activeStep - 1]));
					} else {
						dispatch(togglePage(urlback));
					}
				}}
				noCancel={_.includes([0, 3], activeStep)}
				handleCancel={() => {
					setOpenCancelDialog(true);
				}}
			/>
			<Page>
				<Stack spacing={{ sm: 5, xs: 1 }}>
					{!isMobile && <CreateHeader />}
					<Stack spacing={{ sm: 5, xs: 1 }}>
						<Stepper activeStep={activeStep} total={steps} stepsPrefix={'page.pay.create.step'} />
						<CenteredNarrowContent>
							{isLoadingPaymentData ? (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										minHeight: '400px',
									}}
								>
									<CircularProgress />
								</Box>
							) : (
								<Fragment>
									{_.isEqual(activeStep, 0) && <Step1 {...stepProps} />}
									{_.isEqual(activeStep, 1) && <Step2 {...stepProps} />}
									{_.isEqual(activeStep, 2) && <Step3 {...stepProps} _payment={fullPaymentData} />}
									{_.isEqual(activeStep, 3) && <Step4 _payment={fullPaymentData} />}
								</Fragment>
							)}
						</CenteredNarrowContent>
					</Stack>
					<CancelDialog
						{...dialogPropsTranslated}
						open={openCancelDialog}
						handleContinue={() => {
							setOpenCancelDialog(false);
							if (isLeavingPage) {
								dispatch(togglePage());
							}
						}}
						handleCancel={() => {
							setOpenCancelDialog(false);
							dispatch(toggleUpserting(false));
							if (!isLeavingPage) {
								navigate(`/${pagesEnum.PAY}`);
							} else {
								navigate(ui.to);
							}
						}}
					/>
				</Stack>
			</Page>
		</Fragment>
	);
}
